/* @flow */
/**
 * Action creator for the 'SET_FORM_VALUE' action. This action is used by forms to set modified form values to the state.
 * @param {String} identifier - The reference in the state whose value this action will affect
 * @param {String} value - The form element value that should be set on the state for identifier
 * @param {String} stateKey - The stateKey for the form that defines the form element
 * @param {Boolean} sendTouch - Should the element send a touch along with setting the value
 * @returns {Object} - An object describing the 'SET_FORM_VALUE` action
 */
export const setFormValue = (identifier: string, value: string, stateKey: string, sendTouch: boolean = true) => {
    return {
        type: 'SET_FORM_VALUE',
        identifier,
        value,
        sendTouch,
        stateKey
    };
};

/**
 * Action creator for the 'SET_VALUE' action. This action is used by forms to set modified form values to the state.
 * @param {String} identifier - The reference in the state whose value this action will affect
 * @param {String} value - The form element value that should be set on the state for identifier
 * @param {String} stateKey - The stateKey for the form that defines the form element
 * @returns {Object} - An object describing the 'SET_VALUE` action
 */
export const setValue = (identifier: string, value: string, stateKey: string) => {
    return {
        type: 'SET_VALUE',
        identifier,
        value,
        stateKey
    };
};

/**
 * Action creator for the 'SET_VALUES' action. This action is used by forms to set modified form values to the state.
 * @param {Object} values - map of IDs to values to set on state
 * @param {String} stateKey - The stateKey for the form that defines the form element
 * @returns {Object} - An object describing the 'SET_VALUES` action
 */
export const setValues = (values: Object, stateKey: string) => {
    return {
        type: 'SET_VALUES',
        values,
        stateKey
    };
};

/**
 * Action creator for the 'SET_VALUE_IN_ARRAY' action. This action is used by forms to add/remove value to/from an array in store
 * i.e. checkboxes to store as array rather than key, value pair
 * @param {String} identifier - The reference in the state whose value this action will affect
 * @param {String} value - The form element value that should be set on the state for identifier
 * @param {String} stateKey - The stateKey for the form that defines the form element
 * @param {Boolean} sendTouch - Should the element send a touch along with setting the value
 * @returns {Object} - An object describing the 'SET_VALUE_IN_ARRAY` action
 */
export const setValueInArray = (identifier: string, value: string, stateKey: string, sendTouch: boolean = true) => {
    return {
        type: 'SET_VALUE_IN_ARRAY',
        identifier,
        value,
        stateKey,
        sendTouch
    };
};

/**
 * Action creator for the 'RESET_VALUES' action. This action is used by form groups to reset the values of their
 * form elements when they have been hidden.
 * @param {Object} action - The action object
 * @param {String} stateKey - The stateKey for the form that defines the form element
 * @returns {Object} -  An object describing the 'RESET_VALUES` action
 */
export const resetValues = (action: Object, stateKey: string) => {
    return {
        type: 'RESET_VALUES',
        identifiers: action.identifiers,
        stateKey
    };
};

/**
 * Action creatror for the 'RESET_VALUE_IF_SET' action. This action is used by form elements to reset their state to a default
 * but only if the value is not already null on state
 * @param {String} identifier - The state value to reset
 * @param {String} value - The value of the form element - reset if the state value === this value
 * @param {String} stateKey - The stateKey for the form that defines the form element
 * @returns {Object} -  An object describing the 'RESET_VALUE_IF_SET` action
 */
export const resetValueIfSet = (identifier: string, value: string, stateKey: string) => {
    return {
        type: 'RESET_VALUE_IF_SET',
        identifier,
        value,
        stateKey
    };
};

/**
 * Action creator for the 'RESET_FORM_STATE_VALUES_IF_SET' action. This action is used by form elements to reset their state to defaultValue
 * but only if the value is not already null on state
 * @param {String} identifier - The state value to reset
 * @param {String} defaultValue - The value of the form element to be set to
 * @param {String} value - The value of the form element - reset if the state value === this value
 * @param {String} stateKey - The stateKey for the form that defines the form element
 * @returns {Object} -  An object describing the 'RESET_VALUE_IF_SET` action
 */
export const resetFormValuesIfSet = (identifier: string, defaultValue: string, value: string, stateKey: string) => {
    return {
        type: 'RESET_FORM_STATE_VALUES_IF_SET',
        identifier,
        value,
        defaultValue,
        stateKey
    };
};

/**
 * Action creator for the 'SHOW_FORM_GROUP' action. This action is used by form groups update the state when they
 * have been made visible.
 * @param {Object} fieldSet - The identifier for the form group that is now visible
 * @param {String} stateKey - The stateKey for the form that defines the form element
 * @returns {Object} - An object describing the 'SHOW_FORM_GROUP` action
 */
export const showFieldSet = (fieldSet: Object, stateKey: string) => {
    return {
        type: 'SHOW_FIELD_SET',
        fieldSet,
        stateKey
    };
};

/**
 * Action creator for the 'HIDE_FORM_GROUP' action. This action is used by form groups update the state when they
 * have been made hidden.
 * @param {Object} fieldSet - The identifier for the form group that is now hidden
 * @param {String} stateKey - The stateKey for the form that defines the form element
 * @returns {Object} - An object describing the 'HIDE_FORM_GROUP` action
 */
export const hideFieldSet = (fieldSet: Object, stateKey: string) => {
    return {
        type: 'HIDE_FIELD_SET',
        fieldSet,
        stateKey
    };
};

/**
 * Action creator for the 'ELEMENT_TOUCHED' action. This action is used for validation
 * so that we know which fields the user has attempted to add values for
 * @param {Object} identifier - The identifier for the element that was touched
 * @param {String} stateKey - The stateKey for the form that defines the form element
 * @returns {Object} - An object describing the 'ELEMENT_TOUCHED` action
 */
export const elementTouched = (identifier: Object, stateKey: string) => {
    return {
        type: 'ELEMENT_TOUCHED',
        identifier,
        stateKey
    };
};

/**
 * Action creator for the 'RESET_TOUCHES' action. This action is used for setting an element as 'untouched'
 * @param {Object} identifiers - The identifiers for elements touches to reset
 * @param {String} stateKey - The stateKey for the form that defines the form element
 * @returns {Object} - An object describing the 'RESET_TOUCHES` action
 */
export const resetTouches = (identifiers: Object, stateKey: string) => {
    return {
        type: 'RESET_TOUCHES',
        identifiers,
        stateKey
    };
};

/**
 * Action creator for the 'SET_FORM_VALUES_FROM_STATE' action. This action is used to copy one part of the state tree
 * to another part - ie residentialAddress to postalAddress
 * @param {Object} props - An object with source and target identifiers specified
 * @param {String} stateKey - The stateKey for the form that defines the form element
 * @returns {Object} - An object describing the 'SET_FORM_VALUES_FROM_STATE` action
 */
export const setFormValuesFromState = (props: Object, stateKey: string) => {
    return {
        type: 'SET_FORM_VALUES_FROM_STATE',
        source: props.source,
        target: props.target,
        stateKey
    };
};

/**
 * Action creator for the 'RESET_FORM_STATE_VALUES' action. This action is used to reset a part of the state tree to
 * null values - eg when postalAddress is different to residentialAddress
 * @param {Object} props - An object with the identifier of the state tree branch to be reset
 * @param {String} stateKey - The stateKey for the form that defines the form element
 * @returns {Object} - An object describing the 'RESET_FORM_STATE_VALUES` action
 */
export const resetFormStateValues = (props: Object, stateKey: string) => {
    return {
        type: 'RESET_FORM_STATE_VALUES',
        identifier: props.identifier,
        stateKey,
        exceptions: props.exceptions
    };
};

// collect actions in this export so we can easily import the whole set of actions outside of ctm-components
export const FormActions = {
    resetFormValuesIfSet,
    setFormValue,
    setValue,
    setValues,
    resetValues,
    resetValueIfSet,
    showFieldSet,
    hideFieldSet,
    elementTouched,
    setFormValuesFromState,
    resetFormStateValues
};
